import React from 'react';
import PropTypes from 'prop-types';
import i18n from '../../utils/i18n';
import {issueAttributesType, oxygenType, oxygenUserType} from '../../constants/proptype-validation';
import {ISSUE_ATTRIBUTE_NAMES} from '../../constants/issue-attribute-names';
import {getOxygenEntityInfo} from '../../utils/assigned-to-helpers';
import {getUserCompanyName} from '../../utils/userHelpers';
import IssueFieldInlineEdit from './IssueFieldInlineEdit';
import SelectBoxAdapter from '../common/SelectBoxAdapter';
import {
  getActiveUsers,
  getCurrentUserInMcFormat,
  getProjectCompanies,
  getProjectRoles,
  getAllOxygenMappings,
  getEntityUserNameGetter
} from '../../app/selectors';
import {connect} from '../../app/connect';
import {useAllowedAssignees} from './useAllowedAssignees';
import {HQ_USER_TYPE} from '../../constants/hq-types';

const AssignedToInlineEdit = ({
  issue,
  users,
  companies,
  roles,
  currentUser,
  oxygen,
  className,
  styleType,
  isRequired,
  getEntityDisplayNameById
}) => {
  const {options, clearable} = useAllowedAssignees({users, companies, roles, currentUser, oxygen, issue});
  return (
    <IssueFieldInlineEdit
      data-testid="AssignedToInlineEdit"
      issue={issue}
      className={className}
      styleType={styleType}
      attributeName={ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO}
      label={i18n.t('assignment.to')}
      getDisplayValue={currentValue => {
        if (currentValue) {
          if (currentValue?.type === HQ_USER_TYPE) {
            return getEntityDisplayNameById(currentValue.id, i18n.t('activity.formerUser'), null);
          }
          const selectedOxygenEntity = getOxygenEntityInfo(oxygen, currentValue.type, currentValue.id);
          if (selectedOxygenEntity) {
            return selectedOxygenEntity.name;
          }
        }
        return '';
      }}
      placeholder={i18n.t('assignment.assigneeUnspecified')}
      getFieldValue={issueObj => ({
        id: issueObj[ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO],
        type: issueObj[ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO_TYPE]
      })}
      getIssueAttributes={value => ({
        [ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO]: value?.id ?? null,
        [ISSUE_ATTRIBUTE_NAMES.ASSIGNED_TO_TYPE]: value?.type ?? null
      })}
    >
      {(currentValue, closeEditMode, updateIssue) => (
        <SelectBoxAdapter
          selectedOptions={{oxygenId: currentValue?.id}}
          labelKey="name"
          valueKey="oxygenId"
          dropdownMaxWidth={350}
          subtextGetter={getUserCompanyName}
          autofocus
          grouped
          backspaceRemoves={false}
          options={options}
          searchable
          searchIcon
          searchPlaceholder={i18n.t('filter.assignedToSearch')}
          onClose={closeEditMode}
          onChange={option => {
            const newValue = option && {id: option.oxygenId, type: option.oxygenType};
            updateIssue(newValue);
          }}
          clearable={!isRequired && clearable}
        />
      )}
    </IssueFieldInlineEdit>
  );
};

AssignedToInlineEdit.displayName = 'AssignedToInlineEdit';

AssignedToInlineEdit.propTypes = {
  issue: issueAttributesType.isRequired,
  users: PropTypes.array.isRequired,
  companies: PropTypes.array.isRequired,
  roles: PropTypes.array.isRequired,
  oxygen: oxygenType.isRequired,
  currentUser: oxygenUserType.isRequired,
  className: PropTypes.string,
  styleType: PropTypes.oneOf(Object.values(IssueFieldInlineEdit.StyleTypes)),
  isRequired: PropTypes.bool,
  getEntityDisplayNameById: PropTypes.func.isRequired
};

const mapStateToProps = state => ({
  users: getActiveUsers(state),
  companies: getProjectCompanies(state),
  roles: getProjectRoles(state),
  oxygen: getAllOxygenMappings(state),
  currentUser: getCurrentUserInMcFormat(state),
  getEntityDisplayNameById: getEntityUserNameGetter(state)
});

export default connect(mapStateToProps)(AssignedToInlineEdit);
